/**
 * Shared audit-event table (F-58): the event rows + per-event "hash inputs" modal that
 * re-computes `row_hash` in the browser with the CANONICAL @argus/audit implementation
 * (CC-11). Used by the firm-wide audit log (`/settings/audit`); the same row layout as the
 * per-case audit page, plus an optional Case column linking to that case's audit trail.
 */
import { Check, Copy } from '../ui/icons.ts';
import { Fragment, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { Link } from '@tanstack/react-router';

import { computeRowHash } from '@argus/audit/row-hash';

import { Button, Modal, Notice, Table, Td, Th } from '../ui/index.ts';
import { getErrorMessage, type AuditEventWire } from '../../lib/api.ts';
import { formatDate } from '../../lib/format/date.ts';

import '../../styles/audit.css';

const COPIED_MS = 1500;

export function shortHash(hash: string): string {
  return `${hash.slice(0, 10)}…${hash.slice(-6)}`;
}

/** `document_uploaded` → "Document uploaded" (sentence case, design-system.md §15). */
export function humaniseToken(token: string): string {
  const words = token.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The §2.1 hash-input rows the modal lists, in the EXACT hashed order. */
function hashInputRows(event: AuditEventWire): Array<[string, string]> {
  return [
    ['prev_hash', event.prev_hash],
    ['event_id', event.event_id],
    ['sequence', String(event.sequence)],
    ['event_type', event.event_type],
    ['actor_user_id', event.actor_user_id ?? '(null → empty string)'],
    ['actor_session_id', event.actor_session_id ?? '(null → empty string)'],
    ['resource_type', event.resource_type ?? '(null → empty string)'],
    ['resource_id', event.resource_id ?? '(null → empty string)'],
    ['payload_json (JCS canonical)', event.payload_json],
    ['ip_address', event.ip_address ?? '(null → empty string)'],
    ['user_agent', event.user_agent ?? '(null → empty string)'],
    ['timestamp (unix ms)', String(event.timestamp_ms)],
  ];
}

export function HashInputsModal({
  event,
  onClose,
}: {
  event: AuditEventWire;
  onClose: () => void;
}): JSX.Element {
  const [recomputed, setRecomputed] = useState<string | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // THE client-side re-compute (CC-11): rebuild the pre-hash row from the
    // served fields and run the canonical §2.1 formula in the browser.
    computeRowHash({
      event_id: event.event_id,
      sequence: event.sequence,
      event_type: event.event_type,
      actor_user_id: event.actor_user_id,
      actor_session_id: event.actor_session_id,
      case_id: event.case_id,
      resource_type: event.resource_type,
      resource_id: event.resource_id,
      payload_json: event.payload_json,
      ip_address: event.ip_address,
      user_agent: event.user_agent,
      timestamp: event.timestamp_ms,
      prev_hash: event.prev_hash,
      anchored_in: event.anchored_in,
    })
      .then((hash) => {
        if (!cancelled) setRecomputed(hash);
      })
      .catch((err: unknown) => {
        if (!cancelled) setRecomputeError(getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [event]);

  const match = recomputed !== null && recomputed === event.row_hash;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Hash inputs — sequence ${String(event.sequence)}`}
      description={
        <>
          {humaniseToken(event.event_type)} (<code>{event.event_type}</code>). The exact §2.1 field
          set, in hashed order, joined by the U+001F separator. <code>case_id</code> is deliberately
          outside the hashed set.
        </>
      }
    >
      <div className="stack">
        <dl className="argus-dl argus-audit-dl">
          {hashInputRows(event).map(([name, value]) => (
            <Fragment key={name}>
              <dt>{name}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>

        {recomputeError ? (
          <Notice tone="warning">
            <span className="argus-audit-verdict">Re-compute failed: {recomputeError}</span>
          </Notice>
        ) : recomputed === null ? (
          <Notice role="status">Re-computing SHA-256 in your browser…</Notice>
        ) : match ? (
          <Notice tone="success">
            <span className="argus-audit-verdict">
              Re-computed in your browser: <code>{recomputed}</code> — MATCHES the stored row_hash ✓
            </span>
          </Notice>
        ) : (
          <Notice tone="warning">
            <span className="argus-audit-verdict">
              Re-computed <code>{recomputed}</code> ≠ stored <code>{event.row_hash}</code> —
              MISMATCH ✗ (report this immediately)
            </span>
          </Notice>
        )}

        <p className="argus-audit-stored">
          Stored row_hash: <code>{event.row_hash}</code>
        </p>
      </div>
    </Modal>
  );
}

/** The audit rows table. Owns the inspect modal and the copy-hash affordance. */
export function AuditEventTable({
  events,
  showCase = false,
}: {
  events: AuditEventWire[];
  /** Render the Case column (the firm-wide log; rows carry `case_number`). */
  showCase?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState<AuditEventWire | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (copiedId === null) return;
    const timer = window.setTimeout(() => setCopiedId(null), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copiedId]);

  async function copyHash(event: AuditEventWire): Promise<void> {
    try {
      await navigator.clipboard?.writeText(event.row_hash);
      setCopiedId(event.event_id);
    } catch {
      /* clipboard unavailable: the full hash is in the cell's title and the modal */
    }
  }

  return (
    <>
      <Table dense stickyHeader caption="Audit events">
        <thead>
          <tr>
            <Th numeric shrink>
              Seq
            </Th>
            <Th>Timestamp</Th>
            <Th>User</Th>
            <Th>Action</Th>
            {showCase ? <Th>Case</Th> : null}
            <Th>Target</Th>
            <Th>Row hash</Th>
            <Th aria-label="Actions" className="argus-audit-row-actions">
              <span className="sr-only">Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.event_id}>
              <Td numeric mono>
                {event.sequence}
              </Td>
              <Td>
                <time dateTime={event.timestamp} title={event.timestamp}>
                  {formatDate(event.timestamp, { withSeconds: true })}
                </time>
              </Td>
              <Td title={event.actor_user_id ?? undefined}>
                {event.actor_name ?? event.actor_user_id ?? 'System'}
              </Td>
              <Td title={event.event_type}>{humaniseToken(event.event_type)}</Td>
              {showCase ? (
                <Td mono>
                  {event.case_number ? (
                    <Link
                      to="/cases/$caseNumber/audit"
                      params={{ caseNumber: event.case_number }}
                      aria-label={`Open audit trail of case ${event.case_number}`}
                    >
                      {event.case_number}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Td>
              ) : null}
              <Td mono title={event.resource_id ?? undefined}>
                {event.resource_type
                  ? `${event.resource_type}:${shortHash(event.resource_id ?? '')}`
                  : '—'}
              </Td>
              <Td mono>
                <span className="argus-audit-hash">
                  <span className="argus-audit-hash__text" title={event.row_hash}>
                    {shortHash(event.row_hash)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label="Copy row hash"
                    title={copiedId === event.event_id ? 'Copied' : 'Copy row hash'}
                    className={copiedId === event.event_id ? 'is-copied' : undefined}
                    icon={
                      copiedId === event.event_id ? (
                        <Check size={14} aria-hidden="true" />
                      ) : (
                        <Copy size={14} aria-hidden="true" />
                      )
                    }
                    onClick={() => void copyHash(event)}
                  />
                </span>
              </Td>
              <Td className="argus-audit-row-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Inspect event ${String(event.sequence)}`}
                  onClick={() => setOpen(event)}
                >
                  Inspect
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {open ? <HashInputsModal event={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
